/**
 * Centralized Notification Service
 * Handles all email notifications for the application
 */

import { supabase } from '../lib/supabase';

class NotificationService {
  constructor() {
    // EmailJS Configuration
    this.emailjsPublicKey = 'fO2OoJPQ4GZrkJp7N';
    this.emailjsServiceId = 'service_i57i9hh';
    this.emailjsTemplateId = 'template_99xkhdd';
    this.ownerEmail = 'wayne@paradise-performance.com';
    this.emailjsInitialized = false;
  }

  /**
   * Initialize EmailJS (only once)
   */
  async initEmailJS() {
    if (this.emailjsInitialized) return;
    
    try {
      const emailjs = (await import('emailjs-com')).default;
      emailjs.init(this.emailjsPublicKey);
      this.emailjsInitialized = true;
      console.log('✅ EmailJS initialized');
    } catch (error) {
      console.error('Failed to initialize EmailJS:', error);
    }
  }

  /**
   * Send notification for vehicle deposit
   */
  async sendDepositNotification(paymentData) {
    await this.initEmailJS();
    
    try {
      const emailjs = (await import('emailjs-com')).default;
      
      const templateParams = {
        to_email: this.ownerEmail,
        from_name: paymentData.customer?.name || 'Customer',
        from_email: paymentData.customer?.email || 'Not provided',
        from_phone: paymentData.customer?.phone || 'Not provided',
        message: `
          💰 NEW DEPOSIT RECEIVED!
          
          Customer: ${paymentData.customer?.name || 'Unknown'}
          Email: ${paymentData.customer?.email || 'Not provided'}
          Phone: ${paymentData.customer?.phone || 'Not provided'}
          
          Vehicle: ${paymentData.vehicle?.year} ${paymentData.vehicle?.make} ${paymentData.vehicle?.model}
          VIN: ${paymentData.vehicle?.vin || 'Not provided'}
          
          Deposit Amount: $${paymentData.amount}
          Payment Type: ${paymentData.type}
          Transaction ID: ${paymentData.transactionId || 'Processing...'}
          
          Address: ${paymentData.customer?.address || 'Not provided'}
          City: ${paymentData.customer?.city}, ${paymentData.customer?.state} ${paymentData.customer?.zip}
        `,
        subject: `💰 New Deposit - ${paymentData.customer?.name} - $${paymentData.amount}`
      };
      
      await emailjs.send(this.emailjsServiceId, this.emailjsTemplateId, templateParams);
      console.log('✅ Deposit notification sent');
      return { success: true };
    } catch (error) {
      console.error('Failed to send deposit notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send notification for finance application
   */
  async sendFinanceApplicationNotification(applicationData) {
    await this.initEmailJS();
    
    try {
      const emailjs = (await import('emailjs-com')).default;
      
      const templateParams = {
        to_email: this.ownerEmail,
        from_name: `${applicationData.firstName} ${applicationData.lastName}`,
        from_email: applicationData.email || 'Not provided',
        from_phone: applicationData.phone || 'Not provided',
        message: `
          📋 NEW FINANCE APPLICATION!
          
          Applicant: ${applicationData.firstName} ${applicationData.lastName}
          Email: ${applicationData.email || 'Not provided'}
          Phone: ${applicationData.phone || 'Not provided'}
          
          Employment: ${applicationData.employerName || 'Not provided'}
          Monthly Income: $${applicationData.monthlyIncome || 'Not provided'}
          Time Employed: ${applicationData.timeEmployed || 'Not provided'}
          
          Down Payment: $${applicationData.downPayment || '0'}
          SSN: ${applicationData.ssn ? `***-**-${applicationData.ssn.slice(-4)}` : 'Not provided'}
          
          Address: ${applicationData.address || 'Not provided'}
          City: ${applicationData.city}, ${applicationData.state} ${applicationData.zip}
          
          Vehicle Interest: ${applicationData.vehicleId || 'General Inquiry'}
        `,
        subject: `📋 Finance Application - ${applicationData.firstName} ${applicationData.lastName}`
      };
      
      await emailjs.send(this.emailjsServiceId, this.emailjsTemplateId, templateParams);
      console.log('✅ Finance application notification sent');
      return { success: true };
    } catch (error) {
      console.error('Failed to send finance notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send notification for golf cart service appointment
   */
  async sendGolfCartServiceNotification(appointmentData) {
    await this.initEmailJS();
    
    try {
      const emailjs = (await import('emailjs-com')).default;
      
      const templateParams = {
        to_email: this.ownerEmail,
        from_name: appointmentData.customer_name,
        from_email: appointmentData.customer_email || 'Not provided',
        from_phone: appointmentData.customer_phone,
        message: `
          ⛳ NEW GOLF CART SERVICE APPOINTMENT!
          
          Customer: ${appointmentData.customer_name}
          Phone: ${appointmentData.customer_phone}
          Email: ${appointmentData.customer_email || 'Not provided'}
          
          Service Type: ${appointmentData.service_type}
          Cart Make/Model: ${appointmentData.cart_model || 'Not specified'}
          
          Preferred Date: ${appointmentData.preferred_date}
          Preferred Time: ${appointmentData.preferred_time}
          
          Issue Description:
          ${appointmentData.issue_description || 'No description provided'}
          
          Address: ${appointmentData.address || 'Not provided'}
        `,
        subject: `⛳ Golf Cart Service - ${appointmentData.customer_name} - ${appointmentData.service_type}`
      };
      
      await emailjs.send(this.emailjsServiceId, this.emailjsTemplateId, templateParams);
      console.log('✅ Golf cart service notification sent');
      return { success: true };
    } catch (error) {
      console.error('Failed to send golf cart notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send notification for general appointment scheduling
   */
  async sendAppointmentNotification(appointmentData) {
    await this.initEmailJS();
    
    try {
      const emailjs = (await import('emailjs-com')).default;
      
      const templateParams = {
        to_email: this.ownerEmail,
        from_name: appointmentData.name,
        from_email: appointmentData.email || 'Not provided',
        from_phone: appointmentData.phone,
        message: `
          📅 NEW APPOINTMENT SCHEDULED!
          
          Customer: ${appointmentData.name}
          Phone: ${appointmentData.phone}
          Email: ${appointmentData.email || 'Not provided'}
          
          Appointment Type: ${appointmentData.type || 'General'}
          Preferred Date: ${appointmentData.date}
          Preferred Time: ${appointmentData.time}
          
          Vehicle/Service: ${appointmentData.vehicle || appointmentData.service || 'Not specified'}
          
          Notes:
          ${appointmentData.notes || 'No additional notes'}
        `,
        subject: `📅 New Appointment - ${appointmentData.name} - ${appointmentData.type || 'General'}`
      };
      
      await emailjs.send(this.emailjsServiceId, this.emailjsTemplateId, templateParams);
      console.log('✅ Appointment notification sent');
      return { success: true };
    } catch (error) {
      console.error('Failed to send appointment notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send notification for contact form submission
   */
  async sendContactFormNotification(formData) {
    await this.initEmailJS();
    
    try {
      const emailjs = (await import('emailjs-com')).default;
      
      const templateParams = {
        to_email: this.ownerEmail,
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone || 'Not provided',
        message: `
          📧 NEW CONTACT FORM SUBMISSION!
          
          Name: ${formData.name}
          Email: ${formData.email}
          Phone: ${formData.phone || 'Not provided'}
          
          Service Interest: ${formData.service || 'General Inquiry'}
          
          Message:
          ${formData.message}
        `,
        subject: `📧 Contact Form - ${formData.name} - ${formData.service || 'General'}`
      };
      
      await emailjs.send(this.emailjsServiceId, this.emailjsTemplateId, templateParams);
      console.log('✅ Contact form notification sent');
      return { success: true };
    } catch (error) {
      console.error('Failed to send contact notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send notification for test drive request
   */
  async sendTestDriveNotification(requestData) {
    await this.initEmailJS();
    
    try {
      const emailjs = (await import('emailjs-com')).default;
      
      const templateParams = {
        to_email: this.ownerEmail,
        from_name: requestData.name,
        from_email: requestData.email,
        from_phone: requestData.phone,
        message: `
          🚗 NEW TEST DRIVE REQUEST!
          
          Customer: ${requestData.name}
          Phone: ${requestData.phone}
          Email: ${requestData.email}
          
          Vehicle: ${requestData.vehicle}
          VIN: ${requestData.vin || 'Not provided'}
          
          Preferred Date: ${requestData.date}
          Preferred Time: ${requestData.time}
          
          Current Vehicle: ${requestData.currentVehicle || 'Not provided'}
          Trade-In Interest: ${requestData.tradeIn ? 'Yes' : 'No'}
        `,
        subject: `🚗 Test Drive Request - ${requestData.name} - ${requestData.vehicle}`
      };
      
      await emailjs.send(this.emailjsServiceId, this.emailjsTemplateId, templateParams);
      console.log('✅ Test drive notification sent');
      return { success: true };
    } catch (error) {
      console.error('Failed to send test drive notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send notification for trade-in evaluation
   */
  async sendTradeInNotification(tradeData) {
    await this.initEmailJS();
    
    try {
      const emailjs = (await import('emailjs-com')).default;
      
      const templateParams = {
        to_email: this.ownerEmail,
        from_name: tradeData.name,
        from_email: tradeData.email,
        from_phone: tradeData.phone,
        message: `
          🔄 NEW TRADE-IN EVALUATION REQUEST!
          
          Customer: ${tradeData.name}
          Phone: ${tradeData.phone}
          Email: ${tradeData.email}
          
          Trade Vehicle: ${tradeData.year} ${tradeData.make} ${tradeData.model}
          VIN: ${tradeData.vin || 'Not provided'}
          Mileage: ${tradeData.mileage || 'Not provided'}
          Condition: ${tradeData.condition || 'Not specified'}
          
          Interested In: ${tradeData.interestedVehicle || 'Not specified'}
          
          Additional Notes:
          ${tradeData.notes || 'None'}
        `,
        subject: `🔄 Trade-In Request - ${tradeData.name} - ${tradeData.year} ${tradeData.make} ${tradeData.model}`
      };
      
      await emailjs.send(this.emailjsServiceId, this.emailjsTemplateId, templateParams);
      console.log('✅ Trade-in notification sent');
      return { success: true };
    } catch (error) {
      console.error('Failed to send trade-in notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send notification for any general form submission
   */
  async sendGeneralNotification(title, data) {
    await this.initEmailJS();
    
    try {
      const emailjs = (await import('emailjs-com')).default;
      
      // Format the data object into a readable string
      const formattedData = Object.entries(data)
        .map(([key, value]) => `${key}: ${value || 'Not provided'}`)
        .join('\n          ');
      
      const templateParams = {
        to_email: this.ownerEmail,
        from_name: data.name || data.customer_name || 'Customer',
        from_email: data.email || data.customer_email || 'Not provided',
        from_phone: data.phone || data.customer_phone || 'Not provided',
        message: `
          📌 ${title.toUpperCase()}
          
          ${formattedData}
        `,
        subject: `📌 ${title}`
      };
      
      await emailjs.send(this.emailjsServiceId, this.emailjsTemplateId, templateParams);
      console.log(`✅ ${title} notification sent`);
      return { success: true };
    } catch (error) {
      console.error(`Failed to send ${title} notification:`, error);
      return { success: false, error: error.message };
    }
  }
}

// Export a singleton instance
export const notificationService = new NotificationService();
